/** localStorage key for Home dashboard "View as" role picker. */
export const HOME_ACTIVE_ROLE_KEY = "home_active_role";
/** Set when a moderator deliberately opens an admin, coach, or player preview. */
export const HOME_ROLE_PREVIEW_KEY = "home_role_preview";

const MODERATOR_PREVIEW_ROLES = ["admin", "coach", "player"];

/** Logged-in user from localStorage, or null after logout / missing key. */
export function readStoredUser() {
  try {
    const raw =
      typeof localStorage !== "undefined" ? localStorage.getItem("user") : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function roleIdSet(u) {
  const ids = Array.isArray(u?.all_roles) ? u.all_roles : [];
  return new Set(ids.map((id) => String(id)));
}

function roleNames(u) {
  const names = Array.isArray(u?.all_role_names) ? u.all_role_names : [];
  return names.map((name) => String(name).toLowerCase().trim());
}

function userIsModeratorAccount(u) {
  if (!u || typeof u !== "object") return false;
  if (u.hasModeratorRole === true) return true;
  if (roleIdSet(u).has("r9")) return true;
  return roleNames(u).some((name) => name === "moderator");
}

/**
 * Roles the account actually holds. A moderator is not offered Admin or Coach
 * unless that invitation exists (r2 / r3), even though their permissions cover
 * those club surfaces.
 * @param {object|null|undefined} u
 * @returns {Array<"admin"|"coach"|"player"|"monitor"|"moderator"|"parent"|"committee">}
 */
export function getHomeRolesFromUser(u) {
  const roles = new Set();
  if (!u || typeof u !== "object") return [];
  const ids = roleIdSet(u);
  const names = roleNames(u);
  const hasRoleIds = ids.size > 0;
  const isModerator = userIsModeratorAccount(u);
  const typeId = Number(u.type_user_id);

  const holdsAdminInvite =
    ids.has("r1") ||
    ids.has("r2") ||
    names.some(
      (name) =>
        name === "admin" ||
        name === "administrator" ||
        name === "super admin" ||
        name === "superadmin"
    ) ||
    typeId === 1 ||
    (typeId === 2 && !isModerator) ||
    (u.hasAdminRole === true && !isModerator);
  if (holdsAdminInvite) roles.add("admin");

  const holdsCoachInvite =
    ids.has("r3") ||
    ids.has("r5") ||
    names.some((name) => name === "coach" || name === "expert coach") ||
    (typeId === 3 && !isModerator) ||
    (u.hasCoachRole === true && !isModerator);
  if (holdsCoachInvite) roles.add("coach");

  if (ids.has("r6") || names.some((name) => name === "monitor") || u.hasMonitorRole === true) {
    roles.add("monitor");
  }
  if (isModerator) roles.add("moderator");
  if (
    ids.has("r10") ||
    names.some((name) => name === "committee" || name === "committee member") ||
    typeId === 6 ||
    u.hasCommitteeRole === true
  ) {
    roles.add("committee");
  }

  const holdsPlayerInvite =
    ids.has("r4") ||
    ids.has("r7") ||
    names.some((name) => name === "player" || name === "guest player") ||
    (typeId === 4 && !isModerator) ||
    ((u.hasPlayerRole === true || u.hasGuestPlayerRole === true) && !isModerator);
  if (holdsPlayerInvite) roles.add("player");

  if (
    ids.has("r8") ||
    names.some(
      (name) => name.includes("parent") || name.includes("tutor") || name.includes("guardian")
    ) ||
    typeId === 5 ||
    u.hasParentRole === true
  ) {
    roles.add("parent");
  }

  if (!hasRoleIds && !names.length && !isModerator) {
    if (typeId === 2) roles.add("admin");
    if (typeId === 3) roles.add("coach");
    if (typeId === 4) roles.add("player");
  }

  return Array.from(roles);
}

/** Moderator can open admin, coach, and player surfaces to check that club. */
export function getHomeRoleSwitcherOptions(u) {
  const held = getHomeRolesFromUser(u);
  if (!held.includes("moderator")) return held;
  const options = [...held];
  for (const preview of MODERATOR_PREVIEW_ROLES) {
    if (!options.includes(preview)) options.push(preview);
  }
  return options;
}

/** True when this role is a moderator preview, not an invitation they received. */
export function isPreviewHomeRole(u, role) {
  if (!userIsModeratorAccount(u)) return false;
  if (!MODERATOR_PREVIEW_ROLES.includes(role)) return false;
  return !getHomeRolesFromUser(u).includes(role);
}

/** True when the account has a parent / guardian role. */
export function userHasParentRole(u) {
  return getHomeRolesFromUser(u).includes("parent") || Number(u?.type_user_id) === 5;
}

/** True when the account actually has a player role (independent of Home "View as"). */
export function userHasPlayerRole(u) {
  return getHomeRolesFromUser(u).includes("player");
}

/** True when the account has an admin / Super Admin role (independent of Home "View as"). */
export function userHasAdminRole(u) {
  return getHomeRolesFromUser(u).includes("admin");
}

function defaultHomeRoleForUser(u) {
  const roles = getHomeRolesFromUser(u);
  if (Number(u?.type_user_id) === 5 || roles.includes("parent")) {
    return "parent";
  }
  // Player accounts (type 4) default to the player dashboard — not coach/admin —
  // even if a leftover coach role flag exists from a Digiping mis-registration.
  if (Number(u?.type_user_id) === 4 && roles.includes("player")) {
    if (roles.includes("moderator")) return "moderator";
    if (u?.hasAdminRole === true && roles.includes("admin")) return "admin";
    if (roles.includes("committee") && !roles.includes("coach")) return "committee";
    // Appointed monitors default to monitor view so Digiping can coach the evening
    if (roles.includes("monitor") && !roles.includes("coach")) return "monitor";
    return "player";
  }
  if (roles.includes("moderator")) return "moderator";
  if (roles.includes("admin")) return "admin";
  if (roles.includes("committee")) return "committee";
  if (roles.includes("coach")) return "coach";
  if (roles.includes("monitor")) return "monitor";
  if (roles.includes("player")) return "player";
  return roles[0] || "player";
}

/**
 * Read validated active Home role from localStorage (same fallback as Home.jsx).
 * @param {object|null|undefined} user
 * @returns {"admin"|"coach"|"player"|"monitor"|"moderator"|"parent"|"committee"}
 */
export function readActiveHomeRole(user) {
  const held = getHomeRolesFromUser(user);
  if (!held.length) return "player";

  let saved = null;
  let previewOn = false;
  try {
    saved = localStorage.getItem(HOME_ACTIVE_ROLE_KEY);
    previewOn = localStorage.getItem(HOME_ROLE_PREVIEW_KEY) === "1";
  } catch {
    /* ignore */
  }
  if (saved && held.includes(saved)) return saved;
  if (saved && previewOn && isPreviewHomeRole(user, saved)) return saved;
  return defaultHomeRoleForUser(user);
}

export function mapTypeUserIdToUserType(typeUserId) {
  if (!typeUserId) return 0;
  if (typeUserId === 1 || typeUserId === 2) return typeUserId === 1 ? 0 : 1;
  if (typeUserId === 3) return 2;
  if (typeUserId === 4) return 4;
  return 0;
}

export function mapHomeRoleToUserType(role, u) {
  const tid = Number(u?.type_user_id);
  const tidOk = Number.isFinite(tid) ? tid : u?.type_user_id;
  if (role === "admin" || role === "committee" || role === "moderator") {
    if (tidOk === 1) return 0;
    return 1;
  }
  // Monitor conducts sessions like coach (userType 2) but Digiping remains coach
  if (role === "coach" || role === "monitor") return 2;
  if (role === "player") return 4;
  return mapTypeUserIdToUserType(Number.isFinite(tid) ? tid : u?.type_user_id);
}

/**
 * Whether the UI should treat the user as a player (not coach/staff).
 * Respects the Home dashboard role picker: dual-role users viewing as coach/admin
 * are not treated as players even when type_user_id is 4 or hasPlayerRole is set.
 *
 * @param {object|null|undefined} user
 * @param {{ homeRole?: "admin"|"coach"|"player"|"monitor"|"moderator"|"parent" }} [options]
 */
export function isPlayerAccount(user, { homeRole } = {}) {
  const active = homeRole ?? (user ? readActiveHomeRole(user) : "player");
  if (
    active === "coach" ||
    active === "admin" ||
    active === "monitor" ||
    active === "moderator" ||
    active === "committee" ||
    active === "parent"
  ) {
    return false;
  }
  if (active === "player") return true;

  if (!user || typeof user !== "object") return true;

  const typeId = Number(user.type_user_id);
  if (typeId === 4) return true;
  if (user.hasPlayerRole === true) return true;
  if (Array.isArray(user.all_roles) && (user.all_roles.includes("r4") || user.all_roles.includes("r7"))) {
    return true;
  }
  return false;
}

export function writeActiveHomeRole(role) {
  try {
    localStorage.setItem(HOME_ACTIVE_ROLE_KEY, role);
    if (isPreviewHomeRole(readStoredUser(), role)) {
      localStorage.setItem(HOME_ROLE_PREVIEW_KEY, "1");
    } else {
      localStorage.removeItem(HOME_ROLE_PREVIEW_KEY);
    }
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("smarttt-home-role-changed"));
  }
}

export function userHasModeratorRole(u) {
  return getHomeRolesFromUser(u).includes("moderator") || u?.hasModeratorRole === true;
}

/**
 * Staff who may mark any club player present, pending, or absent for a
 * championship fixture. Moderators keep this even when Home "View as" is player.
 */
export function canManageChampionshipMatchPresence(user, { homeRole } = {}) {
  const active = homeRole ?? (user ? readActiveHomeRole(user) : "player");
  if (
    active === "coach" ||
    active === "admin" ||
    active === "committee" ||
    active === "moderator"
  ) {
    return true;
  }
  return userHasModeratorRole(user);
}

/** Moderator or Super Admin staff tools (eval/KOT advanced toolbar). */
export function isModeratorStaff(user, { homeRole } = {}) {
  const active = homeRole ?? (user ? readActiveHomeRole(user) : "player");
  if (active === "player") return false;
  if (Number(user?.type_user_id) === 1) return true;
  if (user?.isSuperAdmin === true) return true;
  if (Array.isArray(user?.all_roles) && user.all_roles.includes("r1")) return true;
  return userHasModeratorRole(user);
}

export function userHasCommitteeRole(u) {
  return getHomeRolesFromUser(u).includes("committee") || u?.hasCommitteeRole === true;
}

/** Pingflo chat player-invite card: club admins and committee only. */
export function canManageChatPlayerInvites(homeRole) {
  return homeRole === "admin" || homeRole === "committee" || homeRole === "moderator";
}

/** Admin-only accounts have no Home / Planning / Training. Dual-role admins do. */
export function isAdminOnlyAccount(u) {
  const roles = getHomeRolesFromUser(u);
  const isStaff = roles.includes("admin") || roles.includes("committee");
  return isStaff && !roles.includes("coach") && !roles.includes("player");
}

/** Bottom-nav Home: everyone except admin-only accounts. */
export function canSeeHomeBottomNav(u) {
  return !isAdminOnlyAccount(u);
}

/** Bottom-nav Dashboard: coach, committee, admin, and moderator (not player/parent/guest). */
export function canSeeDashboardBottomNav(u) {
  if (u?.isSuperAdmin === true || Number(u?.type_user_id) === 1) return true;
  const roles = getHomeRolesFromUser(u);
  return (
    roles.includes("admin") ||
    roles.includes("committee") ||
    roles.includes("coach") ||
    roles.includes("moderator")
  );
}

const DASHBOARD_HOME_ROLE_LABEL = {
  admin: "Admin",
  coach: "Coach",
  committee: "Committee Member",
  monitor: "Monitor",
  moderator: "Moderator",
  parent: "Parent",
  player: "Player",
};

/**
 * Staff-facing role badge for Dashboard metric cards / chart.
 * Prefer the active Home "View as" role, then highest staff role — never
 * fall through to "Player" when the user also has admin/coach/committee.
 */
export function getDashboardRoleBadgeLabel(u) {
  if (!u || typeof u !== "object") return "User";
  const roles = getHomeRolesFromUser(u);
  const names = Array.isArray(u.all_role_names) ? u.all_role_names : [];
  const active = readActiveHomeRole(u);

  if (isPreviewHomeRole(u, active)) {
    return `View as ${DASHBOARD_HOME_ROLE_LABEL[active] || active}`;
  }

  if (
    u.isSuperAdmin === true ||
    Number(u.type_user_id) === 1 ||
    names.includes("Super Admin") ||
    (Array.isArray(u.all_roles) && u.all_roles.includes("r1"))
  ) {
    return "Super Admin";
  }

  // Dashboard is a staff surface: prefer the active picker role when staff.
  if (
    active === "admin" ||
    active === "coach" ||
    active === "committee" ||
    active === "monitor" ||
    active === "moderator"
  ) {
    return DASHBOARD_HOME_ROLE_LABEL[active];
  }

  if (roles.includes("admin")) return "Admin";
  if (roles.includes("committee")) return "Committee Member";
  if (roles.includes("coach")) {
    if (names.includes("Expert Coach") || (Array.isArray(u.all_roles) && u.all_roles.includes("r5"))) {
      return "Expert Coach";
    }
    return "Coach";
  }
  if (roles.includes("moderator")) return "Moderator";
  if (roles.includes("monitor")) return "Monitor";

  const tid = Number(u.type_user_id);
  if (tid === 1) return "Super Admin";
  if (tid === 2) return "Admin";
  if (tid === 3) return "Coach";
  if (tid === 6) return "Committee Member";
  if (tid === 5) return "Parent";
  if (tid === 4) return "Player";

  if (names.length) return String(names[0]);
  return DASHBOARD_HOME_ROLE_LABEL[active] || "User";
}

/** True when the user may open the club presence report from Dashboard. */
export function canOpenDashboardPresenceReport(u) {
  if (!u || typeof u !== "object") return false;
  if (u.isSuperAdmin === true || Number(u.type_user_id) === 1) return true;
  const roles = getHomeRolesFromUser(u);
  if (
    roles.includes("admin") ||
    roles.includes("committee") ||
    roles.includes("coach") ||
    roles.includes("moderator")
  ) {
    return true;
  }
  const names = Array.isArray(u.all_role_names) ? u.all_role_names : [];
  return names.some((r) =>
    ["Super Admin", "Admin", "Coach", "Expert Coach", "Committee Member", "Moderator"].includes(r)
  );
}

export function userIsExpertCoach(u) {
  if (!u || typeof u !== "object") return false;
  if (Array.isArray(u.all_roles) && u.all_roles.map(String).includes("r5")) return true;
  const names = Array.isArray(u.all_role_names) ? u.all_role_names : [];
  return names.some((name) => String(name).toLowerCase().trim() === "expert coach");
}

/** First screen after login. Dual-role admins keep Home when viewing as coach/player. */
export function defaultAuthenticatedPath(user) {
  const homeRole = readActiveHomeRole(user);
  if (homeRole === "parent") return "/parent/progress";
  if (
    isAdminOnlyAccount(user) ||
    homeRole === "admin" ||
    homeRole === "committee" ||
    homeRole === "moderator"
  ) {
    return "/Dashboard";
  }
  return "/home";
}
